import React, {useEffect} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import {DayType, getCorrectDayTwoDayDelivery} from '@optimaxdev/utils';
import {useInView} from 'react-intersection-observer';
import {connect} from 'react-redux';

import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {ApplicationStoreType} from 'desktop/store';
import {flags} from 'libs/rollout';
import {ShippingMethodsType, ShippingMethodType} from 'types/shippingMethodsType';

import {SHIPPING_METHODS_CODES} from '../../constants/deliveryStep';
import {
    getShippingMethodList,
    getSelectedShippingMethodCode,
    showSingleShippingMethod,
    getNextDayShippingMethod,
} from '../../store/selectors/shippingMethods/shippingMethods';
import {ShippingMethodItemConnected} from '../shippingMethodItem/shippingMethodItem';
import {ShippingSingleMethod} from '../shippingSingleMethod/shippingSingleMethod';

import s from './shippingMethodsList.scss';

export type PropsType = {
    isSingleMethod?: boolean;
    shippingMethodList: ShippingMethodsType;
    currentShippingMethod: string;
    twoDayDeliveryCode: ShippingMethodType | undefined;
};

/**
 * Shipping Methods List for shipping methods list
 *
 * @constructor
 */
export const ShippingMethodsList: React.FC<PropsType> = ({
    currentShippingMethod,
    isSingleMethod,
    shippingMethodList,
    twoDayDeliveryCode,
}) => {
    const {inView, ref} = useInView({
        threshold: 1,
        triggerOnce: true,
    });
    const twoDayDeliveryDay =
        twoDayDeliveryCode?.additionalData &&
        getCorrectDayTwoDayDelivery(twoDayDeliveryCode?.additionalData?.day);

    const isCheckoutTwoDayDelivery = flags.checkoutTwoDayDelivery.isEnabled();

    useEffect(() => {
        if (twoDayDeliveryCode && twoDayDeliveryDay) {
            if ((isCheckoutTwoDayDelivery && inView) || !isCheckoutTwoDayDelivery) {
                sendDesktopAnalyticsPerSession({
                    event: GENERAL_EXPERIMENT_INDICATION,
                    isInteraction: false,
                    label: {
                        expName: 'checkoutTwoDayDelivery',
                        label: `${isCheckoutTwoDayDelivery}`,
                    },
                });
                sendDesktopAnalyticsPerSession({
                    event: CHECKOUT_DELIVERY_STEP,
                    isInteraction: false,
                    label: {
                        message: `1BD Shipping - ${
                            twoDayDeliveryDay === 'tomorrow' ? 'Tomorrow' : 'Other'
                        } - Eligible`,
                    },
                });
            }
        }
    }, [twoDayDeliveryCode, inView, twoDayDeliveryDay, isCheckoutTwoDayDelivery]);

    if (isSingleMethod && shippingMethodList.length)
        return <ShippingSingleMethod shippingMethod={shippingMethodList[0]} />;

    const twoDayDeliveryMethod = isCheckoutTwoDayDelivery && twoDayDeliveryCode;

    const shippingMethods = shippingMethodList.filter(
        ({code}) => code !== SHIPPING_METHODS_CODES.NEXT_DAY,
    );

    const shippingMethodsTwoDay = shippingMethodList.filter(
        ({code}) =>
            code !== SHIPPING_METHODS_CODES.EXPEDITED && code !== SHIPPING_METHODS_CODES.NEXT_DAY,
    );

    const sortedShippingMethods = [twoDayDeliveryMethod, ...shippingMethodsTwoDay];

    return (
        <div>
            {shippingMethods?.length ? (
                (twoDayDeliveryMethod ? sortedShippingMethods : shippingMethods).map(
                    (item, index) => {
                        return (
                            <ShippingMethodItemConnected
                                twoDayDeliveryDay={
                                    twoDayDeliveryDay as DayType | 'tomorrow' | null | undefined
                                }
                                currentShippingMethod={currentShippingMethod}
                                index={index}
                                item={item as ShippingMethodType}
                                key={(item as ShippingMethodType).code}
                                ref={ref}
                            />
                        );
                    },
                )
            ) : (
                <Loader className={s.loader} />
            )}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    currentShippingMethod: getSelectedShippingMethodCode(store),
    isSingleMethod: showSingleShippingMethod(store),
    shippingMethodList: getShippingMethodList(store),
    twoDayDeliveryCode: getNextDayShippingMethod(store),
});

export const ShippingMethodsListConnected = connect(mapStateToProps)(ShippingMethodsList);
